import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
} from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';

interface KeyboardShortcut {
  keys: string[];
  description: string;
  category: string;
}

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
}

const shortcuts: KeyboardShortcut[] = [
  // Navigation
  { keys: ['Alt', '1'], description: 'Go to Chats', category: 'Navigation' },
  { keys: ['Alt', '2'], description: 'Go to Events', category: 'Navigation' },
  { keys: ['Alt', '3'], description: 'Go to Browse', category: 'Navigation' },
  { keys: ['Alt', '4'], description: 'Go to Directory', category: 'Navigation' },
  { keys: ['Alt', '5'], description: 'Go to Settings', category: 'Navigation' },

  // Actions
  { keys: ['Ctrl', 'K'], description: 'Search', category: 'Actions' },
  { keys: ['Ctrl', 'N'], description: 'New Message/Group', category: 'Actions' },
  { keys: ['Ctrl', 'E'], description: 'Create Event', category: 'Actions' },
  { keys: ['Esc'], description: 'Close Dialog/Modal', category: 'Actions' },

  // Messaging
  { keys: ['Ctrl', 'Enter'], description: 'Send Message', category: 'Messaging' },
  { keys: ['↑', '↓'], description: 'Navigate Threads', category: 'Messaging' },
  { keys: ['Enter'], description: 'Open Selected Thread', category: 'Messaging' },

  // General
  { keys: ['?'], description: 'Show Keyboard Shortcuts', category: 'General' },
  { keys: ['Tab'], description: 'Navigate Forward', category: 'General' },
  { keys: ['Shift', 'Tab'], description: 'Navigate Backward', category: 'General' },
];

const KeyboardShortcutsDialog: React.FC<KeyboardShortcutsDialogProps> = ({ open, onClose }) => {
  const categories = Array.from(new Set(shortcuts.map(s => s.category)));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="keyboard-shortcuts-title"
    >
      <DialogTitle id="keyboard-shortcuts-title">
        <Box display="flex" alignItems="center" gap={1}>
          <KeyboardIcon />
          <Typography variant="h6">Keyboard Shortcuts</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {categories.map((category, index) => (
          <Box key={category} mb={index < categories.length - 1 ? 3 : 0}>
            <Typography variant="subtitle1" fontWeight={600} mb={2} color="primary">
              {category}
            </Typography>
            {shortcuts
              .filter(s => s.category === category)
              .map((shortcut, idx) => (
                <Box
                  key={idx}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={1}
                  px={2}
                  sx={{
                    backgroundColor: idx % 2 === 0 ? 'background.default' : 'transparent',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2">{shortcut.description}</Typography>
                  <Box display="flex" gap={0.5}>
                    {shortcut.keys.map((key, keyIdx) => (
                      <React.Fragment key={keyIdx}>
                        <Chip
                          label={key}
                          size="small"
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            minWidth: '40px',
                          }}
                        />
                        {keyIdx < shortcut.keys.length - 1 && (
                          <Typography variant="body2" sx={{ mx: 0.5 }}>
                            +
                          </Typography>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              ))}
            {index < categories.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KeyboardShortcutsDialog;
